// Správce nahrávání: jedno systémové okno „Nahrávání“ se seznamem souborů, průběhem po souborech i celkem
// (jako při kopírování ve Windows). Všechna nahrávání (plocha, Soubory, Vybrat soubor) jdou tudy.
import { core, upload, ApiError } from '../api';
import type { StorageItem } from '../contract';
import { h } from '../dom';
import { t, tk, fmtSize } from '../i18n';
import { icon, fileIcon } from '../icons';
import type { WindowManager } from '../wm/manager';
import type { IwWindow } from '../wm/window';

interface Job {
  file: File;
  path: string;
  parent: string;
  temp: boolean;
  status: 'wait' | 'run' | 'ok' | 'error';
  pct: number;
  error?: string;
  item?: StorageItem;
  row: HTMLElement;
  bar: HTMLElement;
  state: HTMLElement;
}

let wmRef: WindowManager | null = null;
let win: IwWindow | null = null;
let listEl: HTMLElement | null = null;
let totalBar: HTMLElement | null = null;
let totalText: HTMLElement | null = null;
const queue: Job[] = [];
let running = false;
let closeTimer: number | undefined;

export function configureUploads(wm: WindowManager): void {
  wmRef = wm;
}

/** Zařadí soubory do fronty, otevře okno Nahrávání a vrátí nahrané položky (po dokončení všech zadaných). */
export async function uploadFiles(parent: string, files: FileList | File[], paths?: string[], temp = false, dirs?: string[]): Promise<StorageItem[]> {
  if (dirs?.length && !temp && parent) {
    // složky (včetně prázdných) založit předem; soubory si pak své nadřazené najdou podle cesty
    await core('storage', 'mkdirs', { parent, paths: dirs }).catch(() => undefined);
  }
  const jobs: Job[] = Array.from(files).map((file, i) => {
    const row = h('div', { class: 'up-row' });
    const bar = h('div', { class: 'up-bar' }, h('div', { class: 'up-bar-fill' }));
    const state = h('span', { class: 'up-state muted' }, t('core.upload.waiting'));
    const job: Job = { file, path: paths?.[i] ?? '', parent, temp, status: 'wait', pct: 0, row, bar, state };
    row.append(icon(fileIcon(file.type || null)), h('span', { class: 'up-name' }, (job.path || file.name)), h('span', { class: 'up-size muted' }, fmtSize(file.size)), state, bar);
    return job;
  });
  queue.push(...jobs);
  await ensureWindow();
  for (const j of jobs) listEl?.appendChild(j.row);
  updateTotal();
  void pump();
  await Promise.all(jobs.map((j) => waitFor(j)));
  return jobs.filter((j) => j.item).map((j) => j.item as StorageItem);
}

function waitFor(j: Job): Promise<void> {
  return new Promise((res) => {
    const check = () => (j.status === 'ok' || j.status === 'error' ? res() : window.setTimeout(check, 150));
    check();
  });
}

async function ensureWindow(): Promise<void> {
  window.clearTimeout(closeTimer);
  if (win && win.isConnected) {
    if (win.state === 'minimized') win.unminimize();
    return;
  }
  if (!wmRef) return;
  listEl = h('div', { class: 'up-list' });
  totalBar = h('div', { class: 'up-bar total' }, h('div', { class: 'up-bar-fill' }));
  totalText = h('div', { class: 'up-total muted' });
  const el = h('div', { class: 'view upload-view' }, h('div', { class: 'up-head' }, totalText, totalBar), listEl);
  win = await wmRef.openLocal({
    title: t('core.upload.title'),
    icon: 'upload',
    size: { w: 520, h: 360 },
    build: () => ({ el, canClose: async () => true }),
    onClosed: () => {
      win = null;
      listEl = null;
    },
  });
}

function updateTotal(): void {
  const all = queue.length;
  const done = queue.filter((j) => j.status === 'ok' || j.status === 'error').length;
  const errors = queue.filter((j) => j.status === 'error').length;
  const bytes = queue.reduce((s, j) => s + j.file.size, 0) || 1;
  const sent = queue.reduce((s, j) => s + (j.status === 'ok' ? j.file.size : j.status === 'run' ? (j.file.size * j.pct) / 100 : 0), 0);
  const pct = Math.round((sent / bytes) * 100);
  if (totalBar) (totalBar.firstElementChild as HTMLElement).style.width = pct + '%';
  if (totalText) totalText.textContent = t('core.upload.total', { done, all, pct }) + (errors ? ` · ${t('core.upload.errors', { count: errors })}` : '');
  if (win) win.setTitle(done < all ? `${t('core.upload.title')} ${pct} %` : t('core.upload.title'), 'upload');
  if (done === all && !errors) {
    closeTimer = window.setTimeout(() => {
      void win?.close(true);
      queue.length = 0;
    }, 1500);
  }
}

async function pump(): Promise<void> {
  if (running) return;
  running = true;
  try {
    for (;;) {
      const j = queue.find((x) => x.status === 'wait');
      if (!j) break;
      j.status = 'run';
      j.state.textContent = '0 %';
      try {
        const res = (await upload(j.parent, [j.file], (d, tot) => {
          j.pct = Math.round((d / tot) * 100);
          (j.bar.firstElementChild as HTMLElement).style.width = j.pct + '%';
          j.state.textContent = j.pct + ' %';
          updateTotal();
        }, j.temp, [j.path])) as { items: StorageItem[] };
        j.item = res.items[0];
        j.status = 'ok';
        j.pct = 100;
        (j.bar.firstElementChild as HTMLElement).style.width = '100%';
        j.state.replaceChildren(icon('check', 'icon ok'));
        j.row.classList.add('ok');
      } catch (e) {
        j.status = 'error';
        j.error = tk((e as ApiError).message ?? 'core.err.internal');
        j.state.textContent = j.error;
        j.row.classList.add('error');
      }
      updateTotal();
    }
  } finally {
    running = false;
  }
}
